/**
 * trucking_dispatch — trucking. Shift control for the Overview: the four
 * dispatch figures, the week's movement rail, dispatch's own alerts and the
 * fleet split.
 *
 * THIS IS THE ONLY COPY. It was lifted out of pages/TruckingDashboard.tsx,
 * and that page is retired (fix/trucking-nav): /trucking redirects to `/`, as
 * /rentals did. Its Tomorrow's readiness and Top customers panels were not
 * lifted and went with it.
 *
 * BACK SINCE api#851 (#815). The four figures were once withheld because the
 * API had them wrong: active loads counted signed (final) loads, today's
 * revenue summed a load with no rate as $0, and on shift counted an empty
 * table as 0. Each now arrives right or as null:
 *   - `todays_revenue` is null when any load delivered on the tenant's today
 *     is unpriced, with `todays_unrated_count` saying how many. ALL OR
 *     NOTHING: a dash with the count beside it, never a partial sum.
 *   - `on_shift` is null while the tenant has no hours-of-service logs, so it
 *     reads a dash, not 0.
 * Every figure is the API's; none is recomputed here.
 *
 * Sources:
 *   Active loads   GET /trucking/dispatch/metrics  (active_loads)
 *   To sign        GET /trucking/dispatch/metrics  (bol_pending: delivered,
 *                  not yet signed; "Not yet signed" alone read as every open
 *                  load, which is the BOL list's "Open")
 *   Revenue        GET /trucking/dispatch/metrics  (todays_revenue)
 *   On shift       GET /trucking/dispatch/metrics  (on_shift)
 *   movement rail  GET /trucking/loads, grouped by scheduled_date
 *   needs attention GET /trucking/dispatch/alerts
 *   fleet          GET /trucking/fleet
 *
 * The loads list is unpaginated (trucking-loads.routes.ts returns every row
 * for the company), so a tally of it is a count of the tenant's loads, not of
 * a page.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import { api } from '../../../api/client';
import { unwrap, unwrapArray } from '../../../lib/api-envelope';
import { displayValue } from '../../../features/fieldiq-handoff/contracts';
import { fmtCurrency, parseMoney } from '../../../lib/display-labels';
import { LOADS, missingRateNote } from '../../../lib/missing-rate-note';
import { loadStatusSkin } from '../../../lib/load-status';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../../lib/load-failure';
import {
  calendarDayString,
  formatCalendarDate,
  formatInstantTime,
  parseCalendarDate,
  toCalendarDateString,
  todayCalendarDate,
} from '../../../lib/calendar-date';
import { useTenantTimeZone } from '../../../hooks/useTenantTimeZone';
import LoadStatusBadge from '../../trucking/LoadStatusBadge';
import { LoadFailureNotice } from '../../ui/LoadFailureNotice';
import { WIDGET_EYEBROW, WidgetFigure, WidgetPanel, WidgetPending } from '../WidgetPanel';

interface DispatchMetrics {
  active_loads?: number | null;
  bol_pending?: number | null;
  on_shift?: number | null;
  todays_revenue?: number | string | null;
  todays_unrated_count?: number | null;
}
interface DispatchAlert { id: string; severity: 'critical' | 'warning' | 'info'; title: string; message: string; type: string }
interface LoadRow {
  id: string;
  status: string;
  bol_number?: string | null;
  customer_name?: string | null;
  scheduled_date?: string | null;
  start_time?: string | null;
}
interface FleetUnit { id: string; status?: string }

const RAIL_WEEKS = 2;
const ALERTS_SHOWN = 5;

function addDays(day: string | null, n: number): string | null {
  const d = parseCalendarDate(day);
  if (!d) return null;
  d.setDate(d.getDate() + n);
  return toCalendarDateString(d);
}
function mondayOf(day: string | null): string | null {
  const d = parseCalendarDate(day);
  if (!d) return null;
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return toCalendarDateString(d);
}
const loadDay = (l: LoadRow) => calendarDayString(l.scheduled_date ? String(l.scheduled_date) : null);

const SEVERITY_FILL: Record<DispatchAlert['severity'], string> = {
  critical: 'bg-fi-signal-red',
  warning: 'bg-fi-signal-amber',
  info: 'bg-fi-signal-blue',
};

export default function TruckingDispatchWidget() {
  const navigate = useNavigate();
  const timeZone = useTenantTimeZone();
  const today = todayCalendarDate(timeZone);
  const [week, setWeek] = useState(0);

  const metricsQ = useQuery({
    queryKey: ['trucking-dispatch-metrics'],
    queryFn: () => api.trucking.dispatch.metrics().then((r) => unwrap<DispatchMetrics>(r.data)),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const alertsQ = useQuery({
    queryKey: ['trucking-dispatch-alerts'],
    queryFn: () => api.trucking.dispatch.alerts().then((r) => unwrapArray<DispatchAlert>(r.data)),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const loadsQ = useQuery({
    queryKey: ['trucking-loads', 'dashboard'],
    queryFn: () => api.trucking.loads.list().then((r) => unwrapArray<LoadRow>(r.data)),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const fleetQ = useQuery({
    queryKey: ['trucking-fleet-summary'],
    queryFn: () => api.trucking.fleet.list().then((r) => unwrapArray<FleetUnit>(r.data)),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });

  const metricsFailed = queryLoadFailure(metricsQ) != null;
  const metrics = metricsFailed ? undefined : metricsQ.data ?? undefined;
  const fig = (n: number | null | undefined) => (metricsQ.isLoading && !metricsFailed ? '…' : metricsFailed || !metrics ? '—' : displayValue(n));
  const revenue = metrics ? parseMoney(metrics.todays_revenue) : null;
  const revenueNote = metrics && revenue === null ? missingRateNote(metrics.todays_unrated_count, LOADS) : null;

  const weekStart = addDays(mondayOf(today), week * 7);
  const days = weekStart === null ? [] : Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)!);
  const byDay = new Map<string, LoadRow[]>();
  for (const l of loadsQ.data ?? []) {
    const d = loadDay(l);
    if (!d) continue;
    byDay.set(d, [...(byDay.get(d) ?? []), l]);
  }
  for (const list of Array.from(byDay.values())) list.sort((a, b) => String(a.start_time ?? '').localeCompare(String(b.start_time ?? '')));
  const weekLabel = days.length === 0
    ? '—'
    : `${formatCalendarDate(days[0], { month: 'short', day: 'numeric' })} – ${formatCalendarDate(days[6], { month: 'short', day: 'numeric' })}`;

  const fleet = fleetQ.data;
  const fleetCounts = fleet
    ? {
        active: fleet.filter((f) => (f.status || 'active') === 'active').length,
        maintenance: fleet.filter((f) => f.status === 'maintenance').length,
        total: fleet.length,
      }
    : null;
  const alerts = alertsQ.data;

  return (
    <WidgetPanel widgetKey="trucking_dispatch" eyebrow="Trucking / Shift control" title="Trucking Dispatch" link={{ label: 'Dispatch', to: '/trucking/dispatch' }}>
      <div className="space-y-4 p-4">
        <div className="grid grid-cols-2 gap-x-4 gap-y-5 md:grid-cols-4">
          <WidgetFigure testId="trucking-active-loads" label="Active loads" value={fig(metrics?.active_loads)} sub="Booked to arrived" />
          <WidgetFigure testId="trucking-bol-pending" label="To sign" value={fig(metrics?.bol_pending)} sub="Delivered, not signed" />
          <WidgetFigure
            testId="trucking-revenue"
            label="Revenue"
            value={metricsQ.isLoading && !metricsFailed ? '…' : metricsFailed ? '—' : fmtCurrency(revenue)}
            sub={revenueNote ?? 'Delivered today'}
          />
          <WidgetFigure testId="trucking-on-shift" label="On shift" value={fig(metrics?.on_shift)} sub="Drivers on duty" />
        </div>
        {metricsFailed && <LoadFailureNotice error={queryFailureError(metricsQ)} subject="dispatch figures" className="py-2" />}

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
          {/* Movement rail */}
          <section aria-labelledby="trucking-rail-title" className="min-w-0 rounded-fi-control border border-fieldiq-border">
            <div className="flex items-center justify-between gap-2 border-b border-fieldiq-border px-3 py-2">
              <h3 id="trucking-rail-title" className={WIDGET_EYEBROW}>Movement rail</h3>
              <div className="flex items-center gap-1.5">
                <button type="button" aria-label="Previous week" disabled={week === 0} onClick={() => setWeek((w) => Math.max(0, w - 1))} className="rounded-fi-control border border-fieldiq-border p-1 text-fieldiq-text disabled:opacity-40">
                  <ChevronLeft className="size-3.5" aria-hidden="true" />
                </button>
                <span className="min-w-[7rem] text-center text-xs text-fieldiq-muted">{weekLabel}</span>
                <button type="button" aria-label="Next week" disabled={week === RAIL_WEEKS - 1} onClick={() => setWeek((w) => Math.min(RAIL_WEEKS - 1, w + 1))} className="rounded-fi-control border border-fieldiq-border p-1 text-fieldiq-text disabled:opacity-40">
                  <ChevronRight className="size-3.5" aria-hidden="true" />
                </button>
              </div>
            </div>
            {queryLoadFailure(loadsQ) ? (
              <LoadFailureNotice error={queryFailureError(loadsQ)} subject="loads" className="py-6" />
            ) : loadsQ.isLoading ? (
              <WidgetPending rows={2} />
            ) : (
              <div className="overflow-x-auto">
                <ol className="grid min-w-[980px] grid-cols-7 divide-x divide-fieldiq-border" aria-label={`Loads, ${weekLabel}`}>
                  {days.map((day) => {
                    const dayLoads = byDay.get(day) ?? [];
                    return (
                      <li key={day} className="min-h-[8rem] p-2">
                        <p className="mb-2 flex items-center justify-between text-[0.65rem] font-black uppercase tracking-[0.1em] text-fieldiq-muted">
                          <span>{formatCalendarDate(day, { weekday: 'short' })}</span>
                          <span
                            className={day === today ? 'flex size-6 items-center justify-center rounded-full bg-fi-signal-amber text-fi-signal-ink' : 'flex size-6 items-center justify-center text-fieldiq-text'}
                            aria-label={day === today ? 'Today' : undefined}
                          >
                            {parseCalendarDate(day)!.getDate()}
                          </span>
                        </p>
                        {dayLoads.length === 0 ? (
                          <p className="px-1 text-xs text-fieldiq-muted">No loads</p>
                        ) : (
                          <ul className="space-y-1.5">
                            {dayLoads.map((l) => (
                              <li key={l.id}>
                                {/* The BOL number is the load's name everywhere else (the
                                  * retired dashboard's cards showed it), so the card carries it
                                  * and opens that BOL (fix/trucking-figures-agree). */}
                                <button
                                  type="button"
                                  data-testid="rail-load"
                                  onClick={() => navigate(`/trucking/bols/${l.id}`)}
                                  className={`w-full rounded-fi-control border border-fieldiq-border border-l-4 bg-fieldiq-dark/40 p-1.5 text-left hover:bg-fieldiq-dark/70 ${loadStatusSkin(l.status).rail}`}
                                >
                                  <span className="block text-[0.7rem] text-fieldiq-muted">{formatInstantTime(l.start_time, timeZone) ?? '—'}</span>
                                  <span className="block truncate font-mono text-[0.7rem] font-bold text-fieldiq-text">{l.bol_number || '—'}</span>
                                  <span className="block truncate text-xs font-bold text-fieldiq-text">{l.customer_name || '—'}</span>
                                  <span className="mt-1 block"><LoadStatusBadge status={l.status} /></span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ol>
              </div>
            )}
          </section>

          <div className="space-y-4">
            {/* Dispatch's own alerts */}
            <section aria-labelledby="trucking-alerts-title" className="rounded-fi-control border border-fieldiq-border">
              <h3 id="trucking-alerts-title" className={`${WIDGET_EYEBROW} border-b border-fieldiq-border px-3 py-2`}>Needs attention</h3>
              {queryLoadFailure(alertsQ) ? (
                <LoadFailureNotice error={queryFailureError(alertsQ)} subject="dispatch alerts" className="py-6" />
              ) : alertsQ.isLoading ? (
                <WidgetPending rows={2} />
              ) : !alerts || alerts.length === 0 ? (
                <p className="px-3 py-6 text-center text-sm text-fieldiq-muted">No hours-of-service, certification, delay or unassigned-load alerts.</p>
              ) : (
                <>
                  <ul className="divide-y divide-fieldiq-border">
                    {alerts.slice(0, ALERTS_SHOWN).map((a) => (
                      <li key={a.id} className="flex items-start gap-2.5 px-3 py-2.5">
                        <span className={`mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full text-fi-signal-ink ${SEVERITY_FILL[a.severity] ?? 'bg-fi-signal-slate'}`}>
                          <AlertTriangle className="size-3" aria-hidden="true" />
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-fieldiq-text">{a.title}</p>
                          <p className="text-xs text-fieldiq-muted">{a.message}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                  {alerts.length > ALERTS_SHOWN && (
                    <p className="px-3 pb-2 text-xs text-fieldiq-muted">Showing {ALERTS_SHOWN} of {alerts.length}</p>
                  )}
                </>
              )}
            </section>

            {/* Fleet */}
            <section aria-labelledby="trucking-fleet-title" className="rounded-fi-control border border-fieldiq-border px-3 py-3">
              <h3 id="trucking-fleet-title" className={`${WIDGET_EYEBROW} mb-2`}>Fleet</h3>
              {queryLoadFailure(fleetQ) ? (
                <LoadFailureNotice error={queryFailureError(fleetQ)} subject="fleet vehicles" className="py-4" />
              ) : fleetQ.isLoading ? (
                <WidgetPending rows={1} />
              ) : !fleetCounts || fleetCounts.total === 0 ? (
                <p className="py-3 text-center text-sm text-fieldiq-muted">No vehicles in the fleet registry yet.</p>
              ) : (
                <div className="space-y-2">
                  {[
                    { label: 'Active', count: fleetCounts.active, fill: 'bg-fi-signal-green' },
                    { label: 'In maintenance', count: fleetCounts.maintenance, fill: 'bg-fi-signal-red' },
                  ].map((row) => (
                    <div key={row.label} className="flex items-center gap-3">
                      <p className="w-28 shrink-0 text-xs text-fieldiq-muted">{row.label}</p>
                      <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-fieldiq-dark">
                        <div className={`h-full rounded-full ${row.fill}`} style={{ width: `${(row.count / fleetCounts.total) * 100}%` }} />
                      </div>
                      <p className="w-16 text-right text-xs font-semibold tabular-nums text-fieldiq-text">{row.count} of {fleetCounts.total}</p>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    </WidgetPanel>
  );
}
